import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import AppShell from "../components/AppShell";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { ChevronLeft, Bell, BellOff, MessageSquareWarning, Mail, BookOpen, Globe, Vibrate, Repeat } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { registerServiceWorker, requestPushSubscription, unsubscribePush, testPush, getPushPermission } from "../lib/push";
import { toast } from "sonner";
import T from "../components/T";
import SmsConsentModal from "../components/SmsConsentModal";
import { SUPPORTED_LANGUAGES } from "../i18n";
import { isHapticsEnabled, setHapticsEnabled, lightTap } from "../lib/haptics";

const CYCLE_OPT_KEY = "ss_v11_cycle_recognition";
function isCycleEnabled() {
  try { return window.localStorage.getItem(CYCLE_OPT_KEY) !== "0"; } catch { return true; }
}
function setCycleEnabled(v) {
  try { window.localStorage.setItem(CYCLE_OPT_KEY, v ? "1" : "0"); } catch { /* ignore */ }
}

function Row({ icon: Icon, title, desc, children, testid }) {
  return (
    <div className="rounded-3xl bg-card border border-border p-5 flex items-start gap-4" data-testid={testid}>
      <span className="w-9 h-9 grid place-items-center rounded-full bg-brand/10 text-brand shrink-0">
        <Icon className="w-4 h-4" />
      </span>
      <div className="flex-1">
        <div className="font-medium">{title}</div>
        {desc && <p className="text-sm text-foreground/65 mt-0.5 leading-relaxed">{desc}</p>}
      </div>
      <div className="shrink-0">{children}</div>
    </div>
  );
}

export default function Settings() {
  const navigate = useNavigate();
  const { profile, refresh, setLanguage } = useAuth();
  const { t, i18n } = useTranslation();
  const [busy, setBusy] = useState(false);
  const [pushPerm, setPushPerm] = useState("default");
  const [letterEmail, setLetterEmail] = useState("");
  const [haptics, setHaptics] = useState(isHapticsEnabled());
  const [cycle, setCycle] = useState(isCycleEnabled());
  const [smsConsentOpen, setSmsConsentOpen] = useState(false);

  useEffect(() => {
    registerServiceWorker();
    getPushPermission().then(setPushPerm);
  }, []);

  useEffect(() => {
    setLetterEmail(profile?.signal_letter_email || profile?.email || "");
  }, [profile]);

  const update = async (patch) => {
    setBusy(true);
    try {
      await api.patch("/me/preferences", patch);
      await refresh();
    } catch (e) {
      toast.error(t("settings.save_error"));
    } finally { setBusy(false); }
  };

  /**
   * SMS toggle handler — Twilio compliance gate.
   *
   * Turning ON for the first time (no prior `sms_consent_ack_at`) opens
   * the consent modal. Once acknowledged, future on/off toggles flip the
   * flag directly. Turning OFF never requires re-consent.
   */
  const onToggleSms = (v) => {
    if (v && !profile.sms_consent_ack_at) {
      setSmsConsentOpen(true);
      return;
    }
    update({ sms_consent: v }).then(() => {
      if (v) toast.success(t("sms_consent.toast_enabled"));
      else toast(t("sms_consent.toast_disabled"));
    });
  };

  const confirmSmsConsent = async () => {
    await update({
      sms_consent: true,
      sms_consent_ack_at: new Date().toISOString(),
    });
    setSmsConsentOpen(false);
    toast.success(t("sms_consent.toast_enabled"));
  };

  const togglePush = async (val) => {
    if (val) {
      try {
        await requestPushSubscription();
        setPushPerm("granted");
        await refresh();
        toast.success(t("settings.push_on"));
      } catch (e) {
        toast.error(e.message || t("settings.push_enable_error"));
      }
    } else {
      await unsubscribePush();
      await refresh();
      toast(t("settings.push_off"));
    }
  };

  const sendTest = async () => {
    try {
      const title = i18n.language === "es" ? "Una pequeña respiración. Un pequeño paso." : "Small breath. Small step.";
      const body = i18n.language === "es"
        ? "Sister Signal está aquí cuando necesites un momento de calma."
        : "Sister Signal is here whenever you need a quiet moment.";
      const { data } = await testPush(title, body);
      if (data.sent > 0) toast.success(t("settings.push_sent"));
      else if (data.previewed > 0) toast(t("settings.push_preview"));
      else toast.error(t("settings.push_no_sub"));
    } catch { toast.error(t("settings.push_send_error")); }
  };

  if (!profile) return null;

  return (
    <AppShell>
      <div className="px-8 pt-16 pb-6" data-testid="page-settings">
        <button onClick={() => navigate(-1)} className="flex items-center gap-1 text-sm text-foreground/55 hover:text-brand mb-2">
          <ChevronLeft className="w-4 h-4" /> {t("common.back")}
        </button>
        <p className="font-script text-2xl text-brand">{t("settings.kicker")}</p>
        <h1 className="font-serif text-4xl leading-tight"><T i18nKey="settings.h" /></h1>
        <p className="mt-2 text-foreground/65 text-sm">{t("settings.sub")}</p>
      </div>

      <section className="px-8 space-y-3">
        {/* Language */}
        <h2 className="text-xs uppercase tracking-widest text-foreground/55 mt-2">{t("settings.language_section")}</h2>
        <Row icon={Globe} title={t("settings.language_title")} desc={t("settings.language_desc")} testid="setting-language">
          <span className="text-xs uppercase tracking-widest text-brand" data-testid="setting-language-current">
            {SUPPORTED_LANGUAGES.find((l) => l.code === i18n.language)?.native || "English"}
          </span>
        </Row>
        <div className="grid grid-cols-2 gap-2">
          {SUPPORTED_LANGUAGES.map((l) => (
            <button
              key={l.code}
              data-testid={`lang-switch-${l.code}`}
              onClick={() => setLanguage(l.code)}
              className={`rounded-2xl px-4 py-3 border text-sm transition-all ${
                i18n.language === l.code
                  ? "bg-brand text-white border-brand shadow-sm"
                  : "bg-card border-border hover:border-brand/50"
              }`}
            >
              {l.native}
            </button>
          ))}
        </div>

        {/* Haptics — accessibility / sensory preference */}
        <h2 className="text-xs uppercase tracking-widest text-foreground/55 mt-4">{t("settings.feel_section")}</h2>
        <Row icon={Vibrate} title={t("settings.haptics_title")} desc={t("settings.haptics_desc")} testid="setting-haptics">
          <Switch
            data-testid="switch-haptics"
            checked={haptics}
            onCheckedChange={(v) => {
              setHapticsEnabled(v);
              setHaptics(v);
              // Fire one preview tap so the user feels what they just enabled.
              if (v) lightTap();
            }}
          />
        </Row>

        {/* Recurring pattern recognition — opt-in */}
        <Row
          icon={Repeat}
          title={t("settings.cycle_title")}
          desc={t("settings.cycle_desc")}
          testid="setting-cycle"
        >
          <Switch
            data-testid="switch-cycle"
            checked={cycle}
            onCheckedChange={(v) => { setCycleEnabled(v); setCycle(v); }}
          />
        </Row>

        <h2 className="text-xs uppercase tracking-widest text-foreground/55 mt-4">{t("settings.trusted_circle")}</h2>
        <Row icon={MessageSquareWarning} title={t("settings.sms_title")} desc={t("settings.sms_desc")} testid="setting-sms-consent">
          <Switch data-testid="switch-sms" checked={!!profile.sms_consent} onCheckedChange={onToggleSms} disabled={busy} />
        </Row>

        <h2 className="text-xs uppercase tracking-widest text-foreground/55 mt-4">{t("settings.notifications")}</h2>
        <Row
          icon={pushPerm === "granted" && profile.push_opt_in ? Bell : BellOff}
          title={t("settings.push_title")}
          desc={pushPerm === "denied" ? t("settings.push_blocked") : t("settings.push_desc")}
          testid="setting-push"
        >
          <Switch data-testid="switch-push" checked={!!profile.push_opt_in && pushPerm === "granted"} onCheckedChange={togglePush} disabled={busy || pushPerm === "denied"} />
        </Row>
        <div className="pl-4 pr-1 space-y-2 -mt-1">
          <label className="flex items-center justify-between rounded-2xl bg-card/60 border border-border px-4 py-3" data-testid="setting-push-breath">
            <span className="text-sm">{t("settings.push_breath")}</span>
            <Switch checked={!!profile.push_breath_reminders} onCheckedChange={(v) => update({ push_breath_reminders: v })} disabled={busy} />
          </label>
          <label className="flex items-center justify-between rounded-2xl bg-card/60 border border-border px-4 py-3" data-testid="setting-push-daily">
            <span className="text-sm">{t("settings.push_daily")}</span>
            <Switch checked={!!profile.push_daily_check_in} onCheckedChange={(v) => update({ push_daily_check_in: v })} disabled={busy} />
          </label>
          <label className="flex items-center justify-between rounded-2xl bg-card/60 border border-border px-4 py-3" data-testid="setting-push-missed">
            <span className="text-sm">{t("settings.push_missed")}</span>
            <Switch checked={!!profile.push_missed_checkin} onCheckedChange={(v) => update({ push_missed_checkin: v })} disabled={busy} />
          </label>
          {profile.push_opt_in && (
            <Button data-testid="push-test-btn" onClick={sendTest} variant="outline" className="rounded-full mt-2">
              {t("settings.push_test")}
            </Button>
          )}
        </div>

        <h2 className="text-xs uppercase tracking-widest text-foreground/55 mt-6">{t("settings.letters")}</h2>
        <Row icon={Mail} title={t("settings.letter_title")} desc={t("settings.letter_desc")} testid="setting-letter">
          <Switch data-testid="switch-letter" checked={!!profile.signal_letter_opt_in} onCheckedChange={(v) => update({ signal_letter_opt_in: v })} disabled={busy} />
        </Row>
        {profile.signal_letter_opt_in && (
          <div className="rounded-3xl bg-card border border-border p-5">
            <Label className="text-xs uppercase tracking-wider text-foreground/55">{t("settings.letter_send_to")}</Label>
            <Input
              data-testid="letter-email-input"
              value={letterEmail}
              onChange={(e) => setLetterEmail(e.target.value)}
              onBlur={() => letterEmail && update({ signal_letter_email: letterEmail })}
              placeholder="you@example.com"
              className="mt-1 h-12 rounded-2xl"
            />
            <Button
              data-testid="letter-preview-btn"
              variant="outline"
              onClick={() => navigate("/signal-letter")}
              className="mt-3 w-full rounded-full"
            >
              <BookOpen className="w-4 h-4 mr-2" /> {t("settings.letter_preview")}
            </Button>
          </div>
        )}
      </section>

      <div className="h-10" />

      <SmsConsentModal
        open={smsConsentOpen}
        onCancel={() => setSmsConsentOpen(false)}
        onConfirm={confirmSmsConsent}
      />
    </AppShell>
  );
}
